import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarBlank, CaretDown, CaretRight, Warning, CheckCircle } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientSchedulesPage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/api/client/schedules`, { withCredentials: true });
        setSchedules(data.items || []);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00AEEF]" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="client-schedules-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes echeanciers</h1>
        <p className="text-sm text-[#5E6D66] mt-1">Suivez la progression de vos echeanciers de paiement</p>
      </div>

      {schedules.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-16 text-center">
            <CalendarBlank size={36} className="mx-auto text-[#E8E6E1] mb-3" />
            <p className="text-[#5E6D66]">Aucun echeancier pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4" data-testid="client-schedules-list">
          {schedules.map((s) => {
            const isExpanded = expanded === s.id;
            const paidCount = (s.payments || []).filter(p => p.statut === 'recu').length;
            const totalCount = (s.payments || []).length;
            const progression = s.progression || 0;

            return (
              <Card key={s.id} className="border-[#E8E6E1] overflow-hidden" data-testid={`client-schedule-${s.id}`}>
                <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-[#FAFAF8] transition-colors" onClick={() => setExpanded(isExpanded ? null : s.id)}>
                  {isExpanded ? <CaretDown size={18} className="text-[#5E6D66]" /> : <CaretRight size={18} className="text-[#5E6D66]" />}
                  <div className="flex-1 min-w-0">
                    <span className="font-mono text-sm font-semibold text-[#1A2421]">{s.reference}</span>
                    <p className="text-xs text-[#5E6D66] mt-0.5">Debut: {s.start_date} — {totalCount} echeance(s)</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#1A2421]">{(s.total_amount || 0).toLocaleString('fr-FR')} €</p>
                      <p className="text-[11px] text-[#2D5A4C]">Paye: {(s.total_paid || 0).toLocaleString('fr-FR')} €</p>
                    </div>
                    <div className="w-24">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-medium text-[#5E6D66]">{paidCount}/{totalCount}</span>
                        <span className="text-[10px] font-bold" style={{ color: progression >= 100 ? '#2D5A4C' : '#FF8E00' }}>{progression}%</span>
                      </div>
                      <div className="h-2.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all duration-700 ${progression >= 100 ? 'bg-emerald-500' : progression > 50 ? 'bg-[#2D5A4C]' : 'bg-[#FF8E00]'}`} style={{ width: `${Math.min(100, progression)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-[#E8E6E1] p-4">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#E8E6E1]">
                          <TableHead className="text-[10px] uppercase text-[#5E6D66]">#</TableHead>
                          <TableHead className="text-[10px] uppercase text-[#5E6D66]">Type</TableHead>
                          <TableHead className="text-[10px] uppercase text-[#5E6D66]">Montant</TableHead>
                          <TableHead className="text-[10px] uppercase text-[#5E6D66]">Echeance</TableHead>
                          <TableHead className="text-[10px] uppercase text-[#5E6D66]">Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(s.payments || []).map((p, idx) => {
                          const isOverdue = p.statut !== 'recu' && p.date_echeance && new Date(p.date_echeance) < new Date();
                          return (
                            <TableRow key={p.id} className={`border-[#E8E6E1] ${isOverdue ? 'bg-red-50/60' : ''}`}>
                              <TableCell className="text-xs text-[#5E6D66]">{idx + 1}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-[10px]">{p.type_flux === 'acompte' ? 'Acompte' : `Echeance`}</Badge>
                              </TableCell>
                              <TableCell className="text-xs font-medium text-[#1A2421]">{(p.montant_attendu || 0).toLocaleString('fr-FR')} €</TableCell>
                              <TableCell className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-[#5E6D66]'}`}>
                                {p.date_echeance ? new Date(p.date_echeance).toLocaleDateString('fr-FR') : '-'}
                                {isOverdue && <Warning size={12} className="inline ml-1 text-red-500" />}
                              </TableCell>
                              <TableCell>
                                {p.statut === 'recu' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle size={10} className="mr-0.5" /> Paye</Badge>
                                ) : isOverdue ? (
                                  <Badge className="bg-red-100 text-red-700 text-[10px]"><Warning size={10} className="mr-0.5" /> En retard</Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 text-[10px]">En attente</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
